import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Message } from '../types';
import { adminActivate, adminDelete, adminRestore } from '../api';
import { hashColor, getInitials } from '../utils';

interface Props {
  message: Message;
  secret: string;
  onAction: () => void;
}

export function AdminMessageCard({ message, secret, onAction }: Props) {
  const [acting, setActing] = useState(false);
  const color = hashColor(message.from);
  const initials = getInitials(message.from);

  const isDeleted = !!message.deleted_at;
  const isPending = !message.activated && !isDeleted;
  const isActive = message.activated && !isDeleted;

  const handleAction = async () => {
    setActing(true);
    try {
      if (isPending) await adminActivate(message.id, secret);
      else if (isActive) await adminDelete(message.id, secret);
      else await adminRestore(message.id, secret);
      onAction();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActing(false);
    }
  };

  const actionLabel = isPending ? 'Activate' : isActive ? 'Delete' : 'Restore';
  const actionClass = isPending
    ? 'bg-emerald-600 hover:bg-emerald-700'
    : isActive
    ? 'bg-red-600 hover:bg-red-700'
    : 'bg-amber-600 hover:bg-amber-700';

  const badge = isDeleted
    ? <span className="text-xs px-2 py-0.5 rounded-full bg-red-900/40 text-red-400 border border-red-800/60">DELETED</span>
    : isPending
    ? <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-400 border border-slate-600">PENDING</span>
    : <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-900/40 text-emerald-400 border border-emerald-800/60">ACTIVE</span>;

  return (
    <div className="bg-[#1e1e38] rounded-xl p-4 mb-3 border border-[#2d2d4e]">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
            style={{ backgroundColor: color }}
          >
            {initials}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-slate-200 truncate">{message.from}</span>
              {badge}
            </div>
            <span className="text-slate-600 text-xs">{message.email}</span>
          </div>
        </div>
        <button
          onClick={handleAction}
          disabled={acting}
          className={`flex-shrink-0 px-3 py-1.5 text-white text-sm rounded-lg transition-colors disabled:opacity-40 font-medium ${actionClass}`}
        >
          {acting ? '…' : actionLabel}
        </button>
      </div>

      <div className="mt-3 text-slate-400 text-sm prose prose-invert prose-sm max-w-none line-clamp-3">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.message}</ReactMarkdown>
      </div>

      {message.image_url && (
        <img src={message.image_url} alt="Attached" className="mt-2 h-16 rounded-lg object-cover" />
      )}
      {isDeleted && message.deleted_at && (
        <p className="text-slate-700 text-xs mt-2">
          Deleted {new Date(message.deleted_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}
